import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { signal } = this.#controller;
    if (this.hasAttribute('data-azora-adapt-gallery') && this.dataset.presentation === 'carousel') {
      this.addEventListener('slideshow:select', this.#fitSelectedMedia, { signal });
      this.addEventListener('load', this.#fitSelectedMedia, { capture: true, signal });
      window.addEventListener('resize', this.#fitSelectedMedia, { signal });
      this.#sizeObserver = new ResizeObserver(this.#fitSelectedMedia);
      this.#sizeObserver.observe(this);
      requestAnimationFrame(() => this.#fitSelectedMedia());
    }
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #sizeObserver;
  #fitSelectedMedia = () => {
    const slideshow = this.slideshow;
    const slide = slideshow?.slides?.[slideshow.current || 0];
    const media = slide?.querySelector('.product-media');
    if (!media || !this.clientWidth) return;
    const image = media.querySelector('img');
    const ratio = Number.parseFloat(media.style.getPropertyValue('--ratio')) || (image?.naturalWidth / image?.naturalHeight) || 1;
    const desktop = window.matchMedia('(min-width:750px)').matches;
    const header = Number.parseFloat(getComputedStyle(this).getPropertyValue('--header-group-height')) || 0;
    const cap = this.closest('quick-add-dialog') ? window.innerHeight * .55 : Math.max(240, window.innerHeight - header - 112);
    const width = this.closest('quick-add-dialog') ? Math.max(1, this.clientWidth - (desktop ? 74 : 58)) : this.clientWidth;
    const height = this.closest('quick-add-dialog')
      ? Math.round(desktop ? Math.min(window.innerHeight * .9, 760) - 32 : window.innerHeight * .30)
      : Math.round(Math.min(width / ratio, desktop ? cap : width / ratio));
    const value = height + 'px';
    if (this.style.getPropertyValue('--azora-active-height') !== value) this.style.setProperty('--azora-active-height', value);
  };
  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#sizeObserver?.disconnect();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
